/**
 * Pure validator. Walks the scenario once; emits structured issues with
 * `path` info describing where each issue lives. The editor uses this to
 * surface problems in four UI places; a future `publish` endpoint will use
 * it to gate publish (errors block, warnings inform).
 *
 * Issues are split into errors (scenario broken; learner experience would
 * fail) and warnings (probably wrong but might play). The level distinction
 * is what lets the surfacing prioritize.
 *
 * "Could be false at defaults" checks evaluate the condition against
 * declared variable defaults — cheap, catches the common "I forgot to wire
 * up the variable" case. We deliberately do NOT try to prove unreachability
 * across all reachable states; that's a real solver problem and out of scope.
 */
import { defaultStateFromVariables, filterVisibleChoices } from './evaluator.js';
function typesMatch(declType, value) {
    if (declType === 'number')
        return typeof value === 'number' && Number.isFinite(value);
    if (declType === 'boolean')
        return typeof value === 'boolean';
    if (declType === 'string')
        return typeof value === 'string';
    return false;
}
export function validateScenario(scenario) {
    const issues = [];
    const variables = scenario.variables || {};
    const objectives = scenario.objectives || {};
    const nodes = scenario.nodes || {};
    const startNodeId = scenario.startNodeId;
    const defaultState = defaultStateFromVariables(variables);
    const variableReads = new Set();
    const variableWrites = new Set();
    const taggedObjectives = new Set();
    const referencedNodeIds = new Set([startNodeId]);
    const add = (level, code, message, path) => issues.push({ level, code, message, path });
    const checkCondition = (cond, path) => {
        if (!cond)
            return;
        for (const cmp of cond.comparisons || []) {
            variableReads.add(cmp.variable);
            const decl = variables[cmp.variable];
            if (!decl) {
                add('error', 'UNDECLARED_VARIABLE', `Comparison references undeclared variable "${cmp.variable}"`, path);
                continue;
            }
            if (!typesMatch(decl.type, cmp.value)) {
                add('error', 'TYPE_MISMATCH', `Comparison value type doesn't match "${cmp.variable}" (declared as ${decl.type})`, path);
            }
            const isInequality = ['<', '<=', '>', '>='].includes(cmp.op);
            if (isInequality && decl.type !== 'number') {
                add('error', 'INVALID_OP_FOR_TYPE', `Operator "${cmp.op}" is only valid for number variables; "${cmp.variable}" is ${decl.type}`, path);
            }
        }
    };
    const checkEffects = (effs, path) => {
        if (!effs)
            return;
        for (const e of effs) {
            variableWrites.add(e.variable);
            const decl = variables[e.variable];
            if (!decl) {
                add('error', 'UNDECLARED_VARIABLE', `Effect references undeclared variable "${e.variable}"`, path);
                continue;
            }
            if (!typesMatch(decl.type, e.value)) {
                add('error', 'TYPE_MISMATCH', `Effect value type doesn't match "${e.variable}" (declared as ${decl.type})`, path);
            }
            if (e.op === 'add' && decl.type !== 'number') {
                add('error', 'INVALID_OP_FOR_TYPE', `Operator "add" is only valid for number variables; "${e.variable}" is ${decl.type}`, path);
            }
        }
    };
    // ----- Per-node walk -----
    for (const [nodeId, n] of Object.entries(nodes)) {
        const isStart = nodeId === startNodeId;
        const nodeLabel = n.title || nodeId;
        // Node-level objective tags
        for (const tagId of (n.objectiveTags || [])) {
            taggedObjectives.add(tagId);
            if (!objectives[tagId]) {
                add('error', 'UNDECLARED_OBJECTIVE', `Scene "${nodeLabel}" tag references an objective that no longer exists`, { kind: 'node', nodeId, nodeLabel, sub: 'objective tags' });
            }
        }
        // Variants chain
        const variants = n.variants || [];
        if (variants.length === 0) {
            add('error', 'NO_VARIANTS', 'Scene has no variants', { kind: 'node', nodeId, nodeLabel });
        }
        else {
            const last = variants[variants.length - 1];
            if (last.condition !== null) {
                add('error', 'NO_FALLBACK_VARIANT', 'Last variant must be unconditional (the fallback)', { kind: 'node', nodeId, nodeLabel });
            }
            for (let i = 0; i < variants.length - 1; i++) {
                if (variants[i].condition === null) {
                    add('warning', 'UNREACHABLE_VARIANT', `Variant #${i + 1} has no condition and shadows the fallback`, { kind: 'node', nodeId, nodeLabel, variantIndex: i });
                }
                checkCondition(variants[i].condition, { kind: 'node', nodeId, nodeLabel, variantIndex: i, sub: 'variant condition' });
            }
        }
        // Entry effects
        checkEffects(n.entryEffects, { kind: 'node', nodeId, nodeLabel, sub: 'entry effects' });
        // Choices
        for (const [ci, c] of (n.choices || []).entries()) {
            const choiceLabel = c.label || `Choice #${ci + 1}`;
            const choicePath = { kind: 'choice', nodeId, nodeLabel, choiceId: c.id, choiceIndex: ci, choiceLabel };
            checkCondition(c.visibleIf, { ...choicePath, sub: 'visibility' });
            checkEffects(c.effects, { ...choicePath, sub: 'effects' });
            // Choice-level objective tags
            for (const tagId of (c.objectiveTags || [])) {
                taggedObjectives.add(tagId);
                if (!objectives[tagId]) {
                    add('error', 'UNDECLARED_OBJECTIVE', `Choice "${choiceLabel}" tag references an objective that no longer exists`, { ...choicePath, sub: 'objective tags' });
                }
            }
            // Destinations chain
            const dests = c.destinations || [];
            if (dests.length === 0) {
                add('error', 'NO_FALLBACK_DESTINATION', `Choice "${choiceLabel}" has no destinations`, choicePath);
            }
            else {
                const lastDest = dests[dests.length - 1];
                if (lastDest.condition !== null) {
                    add('error', 'NO_FALLBACK_DESTINATION', `Choice "${choiceLabel}" must end with an unconditional fallback`, choicePath);
                }
                if (lastDest.condition === null && !lastDest.destinationId) {
                    add('warning', 'BROKEN_FALLBACK_DESTINATION', `Choice "${choiceLabel}" fallback has no destination set`, choicePath);
                }
                for (let di = 0; di < dests.length - 1; di++) {
                    if (dests[di].condition === null) {
                        add('warning', 'UNREACHABLE_DESTINATION', `Destination entry #${di + 1} has no condition and shadows the fallback`, { ...choicePath, destinationIndex: di });
                    }
                    if (!dests[di].destinationId) {
                        add('warning', 'BROKEN_CONDITIONAL_DESTINATION', `Conditional destination #${di + 1} has no destination set`, { ...choicePath, destinationIndex: di });
                    }
                    checkCondition(dests[di].condition, { ...choicePath, destinationIndex: di, sub: 'destination condition' });
                }
                for (const d of dests) {
                    if (d.destinationId) {
                        referencedNodeIds.add(d.destinationId);
                        if (!nodes[d.destinationId]) {
                            add('error', 'MISSING_DESTINATION_NODE', `Choice "${choiceLabel}" routes to missing node "${d.destinationId}"`, choicePath);
                        }
                    }
                }
            }
            // Feedback chain — text may be empty; only the chain shape is errored.
            const feedbackVariants = c.feedbackVariants || [];
            if (feedbackVariants.length === 0) {
                add('error', 'NO_FALLBACK_FEEDBACK', `Choice "${choiceLabel}" has no feedback variants`, choicePath);
            }
            else {
                const lastFb = feedbackVariants[feedbackVariants.length - 1];
                if (lastFb.condition !== null) {
                    add('error', 'NO_FALLBACK_FEEDBACK', `Choice "${choiceLabel}" feedback chain must end with an unconditional fallback`, choicePath);
                }
                for (let fi = 0; fi < feedbackVariants.length - 1; fi++) {
                    if (feedbackVariants[fi].condition === null) {
                        add('warning', 'UNREACHABLE_FEEDBACK', `Feedback variant #${fi + 1} has no condition and shadows the fallback`, { ...choicePath, feedbackIndex: fi });
                    }
                    checkCondition(feedbackVariants[fi].condition, { ...choicePath, feedbackIndex: fi, sub: 'feedback condition' });
                }
            }
        }
        if (n.type === 'decision' && (n.choices || []).length === 0) {
            add('warning', 'DECISION_WITH_NO_CHOICES', 'Decision scene has no choices — learner is stuck', { kind: 'node', nodeId, nodeLabel });
        }
        if (n.type !== 'outcome' && (n.choices || []).length > 0) {
            const visibleAtDefault = filterVisibleChoices(n, defaultState);
            if (visibleAtDefault.length === 0) {
                if (isStart) {
                    add('error', 'NO_VISIBLE_CHOICES_AT_START', 'Start scene has no choices visible at default state — learner cannot begin', { kind: 'node', nodeId, nodeLabel });
                }
                else {
                    add('warning', 'NO_VISIBLE_CHOICES_AT_DEFAULT', 'No choices visible at default variable state — possible dead-end', { kind: 'node', nodeId, nodeLabel });
                }
            }
        }
    }
    // ----- Orphan nodes -----
    for (const [nodeId, n] of Object.entries(nodes)) {
        if (nodeId === startNodeId)
            continue;
        if (!referencedNodeIds.has(nodeId)) {
            add('warning', 'ORPHAN_NODE', `No choice routes to "${n.title || nodeId}" — it's unreachable`, { kind: 'node', nodeId, nodeLabel: n.title || nodeId });
        }
    }
    // ----- Variable usage -----
    for (const name of Object.keys(variables)) {
        const isRead = variableReads.has(name);
        const isWritten = variableWrites.has(name);
        if (!isRead && !isWritten) {
            add('warning', 'UNUSED_VARIABLE', `Variable "${name}" is declared but never read or written`, { kind: 'variable', variableName: name });
        }
        else if (!isRead) {
            add('warning', 'WRITE_ONLY_VARIABLE', `Variable "${name}" is written but never read in any condition`, { kind: 'variable', variableName: name });
        }
        else if (!isWritten) {
            add('warning', 'CONSTANT_VARIABLE', `Variable "${name}" is read but never written — value is always the default`, { kind: 'variable', variableName: name });
        }
    }
    // ----- Objective usage -----
    for (const objId of Object.keys(objectives)) {
        if (!taggedObjectives.has(objId)) {
            const obj = objectives[objId];
            add('warning', 'UNUSED_OBJECTIVE', `Objective "${obj.code || objId}" is declared but never tagged on any scene or choice`, { kind: 'objective', objectiveId: objId });
        }
    }
    // ----- Index for fast per-node lookup -----
    const byNodeId = {};
    for (const issue of issues) {
        const nid = issue.path && issue.path.nodeId;
        if (!nid)
            continue;
        if (!byNodeId[nid])
            byNodeId[nid] = [];
        byNodeId[nid].push(issue);
    }
    return {
        issues,
        errors: issues.filter(i => i.level === 'error'),
        warnings: issues.filter(i => i.level === 'warning'),
        byNodeId,
    };
}
//# sourceMappingURL=validator.js.map